import CustomComponent from '../CustomComponent';

export const COUNTER_CHANGE_EVENT = 'counter-change';

let instanceCounter = 0;

export class CounterComponent extends CustomComponent {
	static key = 'lta-counter';

	static get observedAttributes() {
		return ['label', 'value', 'max', 'minimum', 'increment-by'];
	}

	constructor() {
		super();
		this._instanceId = `lta-counter-${++instanceCounter}`;
		this.handleDecrement = this.handleDecrement.bind(this);
		this.handleIncrement = this.handleIncrement.bind(this);
		this.handleInput = this.handleInput.bind(this);
		this.handleBlur = this.handleBlur.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleBeforeInput = this.handleBeforeInput.bind(this);
	}

	getNumericAttribute(name, fallback) {
		const attributeValue = this.getAttribute(name);
		if (attributeValue === null || attributeValue === '') {
			return fallback;
		}

		const parsed = Number(attributeValue);
		return Number.isFinite(parsed) ? parsed : fallback;
	}

	get optionalMinimum() {
		const attributeValue = this.getAttribute('minimum');
		if (attributeValue === null || attributeValue === '') {
			return undefined;
		}

		const parsed = Number(attributeValue);
		return Number.isFinite(parsed) ? parsed : undefined;
	}

	get optionalMaximum() {
		const attributeValue = this.getAttribute('max');
		if (attributeValue === null || attributeValue === '') {
			return undefined;
		}

		const parsed = Number(attributeValue);
		return Number.isFinite(parsed) ? parsed : undefined;
	}

	get currentValue() {
		return this.getNumericAttribute('value', 0);
	}

	get incrementBy() {
		return this.getNumericAttribute('increment-by', 1);
	}

	canDecrement() {
		const minimum = this.optionalMinimum;
		if (minimum === undefined) {
			return true;
		}

		return this.currentValue > minimum;
	}

	canIncrement() {
		const maximum = this.optionalMaximum;
		if (maximum === undefined) {
			return true;
		}

		return this.currentValue < maximum;
	}

	clampValue(value, { clampMaximum = true } = {}) {
		let result = value;
		const minimum = this.optionalMinimum;
		const maximum = this.optionalMaximum;

		if (minimum !== undefined) {
			result = Math.max(minimum, result);
		}

		if (clampMaximum && maximum !== undefined) {
			result = Math.min(maximum, result);
		}

		return result;
	}

	parseInputValue(raw) {
		const parsed = Number(raw);
		if (!Number.isFinite(parsed)) {
			return null;
		}

		return Number.isInteger(this.incrementBy) ? Math.round(parsed) : parsed;
	}

	normalizeValue(value, { clampMaximum = true } = {}) {
		const parsed = this.parseInputValue(value);
		if (parsed === null) {
			return this.currentValue;
		}

		return this.clampValue(parsed, { clampMaximum });
	}

	setValueFromInput(nextValue) {
		const oldValue = this.currentValue;

		if (nextValue === oldValue) {
			this.syncControlState();
			return;
		}

		this.setAttribute('value', String(nextValue));
		this.dispatchEvent(
			new CustomEvent(COUNTER_CHANGE_EVENT, {
				bubbles: true,
				composed: true,
				detail: {
					oldValue,
					newValue: nextValue
				}
			})
		);
		this.syncControlState();
	}

	updateValue(nextValue) {
		const oldValue = this.currentValue;
		const newValue = this.normalizeValue(nextValue);

		if (newValue === oldValue) {
			this.syncControlState();
			return;
		}

		this.setAttribute('value', String(newValue));
		this.dispatchEvent(
			new CustomEvent(COUNTER_CHANGE_EVENT, {
				bubbles: true,
				composed: true,
				detail: {
					oldValue,
					newValue
				}
			})
		);
	}

	handleDecrement() {
		if (!this.canDecrement()) {
			this.syncControlState();
			return;
		}

		this.updateValue(this.currentValue - this.incrementBy);
	}

	handleIncrement() {
		if (!this.canIncrement()) {
			this.syncControlState();
			return;
		}

		const input = this.querySelector('.lta-counter__value');
		const baseValue =
			input && input.value !== '' && input.value !== '-'
				? this.parseInputValue(input.value) ?? this.currentValue
				: this.currentValue;

		this.updateValue(baseValue + this.incrementBy);
	}

	handleInput(event) {
		const raw = event.target.value;
		if (raw === '' || raw === '-') {
			return;
		}

		if (/[eE+]/.test(raw)) {
			event.target.value = String(this.currentValue);
			return;
		}

		const parsed = this.parseInputValue(raw);
		if (parsed === null) {
			return;
		}

		this.setValueFromInput(parsed);
	}

	handleBlur(event) {
		const raw = event.target.value;
		let nextValue;

		if (raw === '' || raw === '-') {
			nextValue = this.optionalMinimum ?? 0;
		} else {
			nextValue = this.parseInputValue(raw) ?? this.currentValue;
		}

		this.setValueFromInput(nextValue);
		event.target.value = String(this.currentValue);
	}

	isBlockedNumericKey(key) {
		if (['e', 'E', '+'].includes(key)) {
			return true;
		}

		const minimum = this.optionalMinimum;
		if (key === '-' && (minimum === undefined || minimum >= 0)) {
			return true;
		}

		if (Number.isInteger(this.incrementBy) && (key === '.' || key === ',')) {
			return true;
		}

		return false;
	}

	handleBeforeInput(event) {
		if (event.data && /[eE+]/.test(event.data)) {
			event.preventDefault();
			return;
		}

		const minimum = this.optionalMinimum;
		if (event.data?.includes('-') && (minimum === undefined || minimum >= 0)) {
			event.preventDefault();
		}
	}

	handleKeyDown(event) {
		if (this.isBlockedNumericKey(event.key)) {
			event.preventDefault();
			return;
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			event.target.blur();
		}
	}

	syncControlState() {
		const input = this.querySelector('.lta-counter__value');
		const decrementBtn = this.querySelector('[data-counter-action="decrement"]');
		const incrementBtn = this.querySelector('[data-counter-action="increment"]');

		if (input && document.activeElement !== input) {
			input.value = String(this.currentValue);
		}

		if (decrementBtn) {
			decrementBtn.disabled = !this.canDecrement();
		}

		if (incrementBtn) {
			incrementBtn.disabled = !this.canIncrement();
		}
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (oldValue === newValue || !this._initialized) {
			return;
		}

		// Keep focus while typing: sync controls in place instead of full re-render.
		if (name === 'value' || name === 'minimum' || name === 'max' || name === 'increment-by') {
			this.syncControlState();
			return;
		}

		this._render();
	}

	render() {
		const label = this.props.label || '';
		const value = this.currentValue;
		const decrementDisabled = !this.canDecrement();
		const incrementDisabled = !this.canIncrement();
		const ariaLabel = label ? ` aria-label="${label}"` : '';
		const minimum = this.optionalMinimum;
		const inputId = `${this._instanceId}-value`;

		return `
			<div class="lta-counter">
				${label ? `<label class="lta-counter__label" for="${inputId}">${label}</label>` : ''}
				<div class="lta-counter__control" role="group"${ariaLabel}>
					<button
						type="button"
						class="lta-counter__button lta-counter__button--decrement"
						data-counter-action="decrement"
						aria-label="Decrease ${label || 'value'}"
						${decrementDisabled ? 'disabled' : ''}
					>−</button>
					<input
						type="number"
						class="lta-counter__value u-border-0"
						id="${inputId}"
						value="${value}"
						step="${this.incrementBy}"
						inputmode="numeric"
						${minimum !== undefined ? `min="${minimum}"` : ''}
						aria-live="polite"
						aria-label="${label || 'Value'}"
					/>
					<button
						type="button"
						class="lta-counter__button lta-counter__button--increment"
						data-counter-action="increment"
						aria-label="Increase ${label || 'value'}"
						${incrementDisabled ? 'disabled' : ''}
					>+</button>
				</div>
			</div>
		`;
	}

	afterRender() {
		this.querySelector('[data-counter-action="decrement"]')?.addEventListener('click', this.handleDecrement);
		this.querySelector('[data-counter-action="increment"]')?.addEventListener('click', this.handleIncrement);

		const input = this.querySelector('.lta-counter__value');
		input?.addEventListener('input', this.handleInput);
		input?.addEventListener('blur', this.handleBlur);
		input?.addEventListener('keydown', this.handleKeyDown);
		input?.addEventListener('beforeinput', this.handleBeforeInput);
	}

	onUnload() {
		this.querySelector('[data-counter-action="decrement"]')?.removeEventListener('click', this.handleDecrement);
		this.querySelector('[data-counter-action="increment"]')?.removeEventListener('click', this.handleIncrement);

		const input = this.querySelector('.lta-counter__value');
		input?.removeEventListener('input', this.handleInput);
		input?.removeEventListener('blur', this.handleBlur);
		input?.removeEventListener('keydown', this.handleKeyDown);
		input?.removeEventListener('beforeinput', this.handleBeforeInput);
	}
}

export default CounterComponent;
